import React from "react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import {
  HandCoins,
  Lock,
  Nfc,
  PercentCircle,
  WalletMinimal,
} from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { Route as ReportAvailableRoute } from "@/routes/(pharma)/_layout/reports/sales-summary/index";
import Details from "./details";
import { CSVLink } from "react-csv";
import { convertToCurrencyRounded } from "@/utils/currency_converter";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { DailySalesResponse } from "@/gen/sales/daily_sales_pb";
dayjs.extend(customParseFormat);

const Component = () => {
  const navigate = useNavigate();
  const searchParams = ReportAvailableRoute.useSearch();
  const data = ReportAvailableRoute.useLoaderData() as DailySalesResponse;
  console.log("Test: ", data);

  const headers = [
    { label: "Gross Sales (Without VAT)", key: "gross_sales_amount" },
    { label: "Discounts", key: "total_discount" },
    { label: "VAT", key: "total_vat" },
    { label: "Net Sales (Including VAT)", key: "total_sales_amount" },
    { label: "Cash Payments", key: "cash_total" },
    { label: "Card Payments", key: "card_total" },
    { label: "Insurance Payments", key: "insurance_total" },
  ];

  const csvData = [
    {
      gross_sales_amount: convertToCurrencyRounded(data.grossSalesAmount),
      total_discount: convertToCurrencyRounded(data.totalDiscount),
      total_vat: convertToCurrencyRounded(data.totalVat),
      total_sales_amount: convertToCurrencyRounded(data.totalSalesAmount),
      cash_total: convertToCurrencyRounded(data.cashTotal),
      card_total: convertToCurrencyRounded(data.cardTotal),
      insurance_total: convertToCurrencyRounded(data.insuranceTotal),
    },
  ];

  
  const formatDate = (dateString: string | number | Date) => {
    const date = new Date(dateString);
    const dayOfWeek = date.toLocaleString("en-US", { weekday: "long" });
    const month = date.toLocaleString("en-US", { month: "long" });
    const day = date.getDate();
    const year = date.getFullYear();
    return `${dayOfWeek} - ${month} ${day}, ${year}`;
  };
  
  const today = searchParams ? formatDate(searchParams.date) : "";
  const filename = `exported_data_${searchParams.date}.csv`;

  return (
    <div>
      <div className="flex items-center justify-between">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                onClick={() => {
                  navigate({
                    to: "/invoicing",
                  });
                }}
                style={{
                  cursor: "pointer",
                }}
              >
                Reports Overview
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Sales Summary</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <CSVLink data={csvData} headers={headers} filename={filename}>
          <Button>Export Sales Summary</Button>
        </CSVLink>
      </div>
      <hr className="my-4" />
      <div className="flex items-center justify-between py-6">
        <p className="font-semibold text-3xl">
          <span className="text-black font-bold">{today}</span>
        </p>
        <div className="flex items-center justify-between text-base font-semibold text-black">
          This summary is generated at {dayjs().format("h:mm A")}
        </div>
      </div>
      <div className="grid grid-cols-6 gap-4 divide-x-2">
        <Details
          icon={<Lock />}
          title="Gross Sales (Without VAT)"
          text={convertToCurrencyRounded(data.grossSalesAmount)}
        />
        <Details
          icon={<PercentCircle />}
          title="Discounts"
          text={convertToCurrencyRounded(data.totalDiscount)}
        />
        <Details
          icon={<PercentCircle />}
          title="VAT"
          text={convertToCurrencyRounded(data.totalVat)}
        />
        <Details
          icon={<HandCoins />}
          title="Net Sales (Including VAT)"
          text={convertToCurrencyRounded(data.grossSalesAmount + data.totalVat)}
        />
        <Details
          icon={<WalletMinimal />}
          title="Cash Payments"
          text={convertToCurrencyRounded(data.cashTotal)}
        />
        <Details
          icon={<Nfc />}
          title="Card Payments"
          text={convertToCurrencyRounded(data.cardTotal)}
        />
      </div>
      <div className="grid-cols-1">
        <hr className="my-4" />
      </div>
      <div className="grid grid-cols-6 gap-4 divide-x-2">
        <Details
          icon={<WalletMinimal />}
          title="Insurance Payments"
          text={convertToCurrencyRounded(data.insuranceTotal)}
        />
      </div>
    </div>
  );
};

export default Component;
